import { useContext, useEffect, useState, useRef } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { getMessageCountForModel } from '@/db/messages'

import {
  getEffectiveFeaturePlanId,
  isLegacyByokPlan,
  normalizePersonalPlanTier
} from '@/lib/billing/losi-pricing'
import { LLM_LIST } from '@/lib/models/llm/llm-list'
import {
  FREE_IMAGE_DAILY_LIMIT,
  FREE_MESSAGE_DAILY_LIMIT,
  PLUS_MESSAGE_DAILY_LIMIT,
  PRO_ULTRA_MESSAGE_DAILY_LIMIT,
  ULTRA_MESSAGE_DAILY_LIMIT
} from '@/lib/subscription'
import { PLAN_FREE, PLAN_PLUS, PLAN_PRO, PLAN_ULTRA } from '@/lib/stripe/config'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'

interface ChatMessageCounterProps {}

const ChatMessageCounter: React.FC<ChatMessageCounterProps> = () => {
  const { t } = useTranslation()
  const { profile, setIsPaywallOpen } = useContext(ChatbotUIContext)
  const scheduleUpgradePaywall = useDelayedPaywall(
    setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined
  )
  const { isGenerating, chatSettings } = useContext(ChatbotUIChatContext)
  const [messageCount, setMessageCount] = useState(0)
  const [imageCount, setImageCount] = useState(0)
  const [isLimitAlertOpen, setIsLimitAlertOpen] = useState(false)
  const [isNearLimit, setIsNearLimit] = useState(false)
  const rawPlan = profile?.plan || PLAN_FREE
  const userPlan = normalizePersonalPlanTier(getEffectiveFeaturePlanId(profile))
  const isByokPlan = isLegacyByokPlan(rawPlan)
  const modelData = LLM_LIST.find(
    (x) => x.modelId === chatSettings?.model || x.hostedId === chatSettings?.model
  )
  const modelTier = modelData?.tier

  let limit = process.env.NEXT_PUBLIC_FREE_MESSAGE_DAILY_LIMIT
    ? parseInt(process.env.NEXT_PUBLIC_FREE_MESSAGE_DAILY_LIMIT)
    : FREE_MESSAGE_DAILY_LIMIT

  let showCounter = true
  if (userPlan === PLAN_ULTRA) {
    if (modelTier === PLAN_ULTRA) {
      limit = ULTRA_MESSAGE_DAILY_LIMIT
    } else {
      showCounter = false
    }
  } else if (userPlan === PLAN_PRO) {
    if (modelTier === PLAN_ULTRA) {
      limit = PRO_ULTRA_MESSAGE_DAILY_LIMIT
    } else {
      showCounter = false
    }
  } else if (userPlan === PLAN_PLUS) {
    limit = PLUS_MESSAGE_DAILY_LIMIT
  }

  if (isByokPlan) {
    showCounter = false
  }

  const upgradePlanLabel =
    userPlan === PLAN_FREE
      ? 'Plus'
      : userPlan === PLAN_PLUS
        ? 'Pro'
        : userPlan === PLAN_PRO
          ? 'Ultra'
          : null

  useEffect(() => {
    if (!profile || isByokPlan) {
      return
    }

    const fetchMessageCount = async () => {
      if (!chatSettings?.model) {
        return
      }

      try {
        const count = await getMessageCountForModel(profile.user_id, chatSettings?.model)

        // Show alert whenever user tries to send a message at or above the limit
        if (count >= limit) {
          setIsLimitAlertOpen(true)
        }
        // Near-limit (80%) soft warning
        const threshold = Math.floor(limit * 0.8)
        setIsNearLimit(count >= threshold && count < limit)

        setMessageCount(count || 0)
      } catch (error) {
        console.error(error)
      }
    }

    fetchMessageCount()
  }, [chatSettings, isByokPlan, isGenerating, limit, profile])

  /* // Removing image count logic due to backend errors
  useEffect(() => {
    if (!profile) {
      return
    }
    const fetchImageCount = async () => {
      if (!chatSettings?.model) {
        return
      }
      try {
        const count = await getImageCountForModel(profile.user_id)
        setImageCount(count || 0)
      } catch (error) {
        console.error(error)
      }
    }
    fetchImageCount()
  }, [profile, isGenerating, chatSettings])
  */

  if (!profile) {
    return null
  }

  if (!showCounter) {
    return null
  }

  return (
    <>
      {/* 80% near-limit banner (soft). */}
      {isNearLimit && (
        <div className="mb-1 mt-2 text-center text-[11px] text-amber-700 dark:text-amber-300">
          {t('You’re nearing today’s free limit.', 'You’re nearing today’s free limit.')}{' '}
          <button
            className="ml-1 font-semibold underline hover:text-amber-900 dark:hover:text-amber-100"
            onClick={() => scheduleUpgradePaywall()}
            type="button"
          >
            {t('Upgrade to keep going', 'Upgrade to keep going')}
          </button>
        </div>
      )}

      <AlertDialog
        open={isLimitAlertOpen}
        onOpenChange={(isOpen: boolean) => {
          setIsLimitAlertOpen(isOpen)
          if (!isOpen && upgradePlanLabel) {
            scheduleUpgradePaywall()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Message Limit Reached</AlertDialogTitle>
            <AlertDialogDescription>
              {upgradePlanLabel ? (
                <>
                  You&apos;ve reached today&apos;s message limit for your current plan. Please come
                  back tomorrow or{' '}
                    <Button
                      onClick={() => {
                        setIsLimitAlertOpen(false)
                        scheduleUpgradePaywall()
                      }}
                      className="px-0 text-xs font-semibold underline"
                      variant="link"
                    >
                    upgrade to {upgradePlanLabel}
                  </Button>{' '}
                  to keep going.
                </>
              ) : (
                'You’ve reached today’s message limit for your current plan. Please come back tomorrow.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export { ChatMessageCounter }
